import React, { useState, useEffect, useRef } from "react";

import ApiSearch from "./components/ApiSearch/ApiSearch.jsx";
import SwitchSearch from "./components/SwitchSearch/SwitchSearch.jsx";

import styles from "./Search.module.scss";
import ClickOutside from "../../helpers/ClickOutSide";
import cn from "classnames";
import {isDesktop} from "../../utils/config";
import {checkPath} from "./components/SwitchSearch/utils/checkPath";
import { isIP, isEmail, getValidations } from "./components/SwitchSearch/utils/validations";
import { cleanExpiredCache } from "../../utils/cache.js";

const Search = (props) => {
    const [query, setQuery] = useState("");
    const [trigger, setTrigger] = useState(false);
    const [opened, setOpened] = useState(false);
    const [activeLine, setActiveLine] = useState(null);
    const [lineNumber, setLineNumber] = useState(0);
    const [lineLinks, setLineLinks] = useState({});

    const ref = useRef(null);
    const submitRef = useRef();
    const inputRef = useRef();
    const clearRef = useRef();
    const mobileTriggerRef = useRef();
    const formRef = useRef();

    const searchIsActiveCssClass = 'is-search-focused';
    const headerBlock = document.querySelector('[data-id="header"]');

    useEffect(() => {
        const pageUrlArray = window.location.pathname.split('/');
        // drop the lang prefix if present
        if (['en', 'ru'].includes(pageUrlArray[1])) {
            pageUrlArray.splice(1, 1);
        }

        const app = pageUrlArray[1] ?? '';
        const address = pageUrlArray[2] ?? '';
        if (
            (app.startsWith('blacklists') && (isIP(address) || isEmail(address)))
            || (app.startsWith('ipinfo') && isIP(address))
            || (app.startsWith('email-checker') && isEmail(address))
        ) {
            presetInput(pageUrlArray[2]);
        }

        if (pageUrlArray[1].startsWith('website-malware-scanner') || pageUrlArray[1].startsWith('uptime-monitoring')) {
            const url = (new URLSearchParams(window.location.search)).get('url');
            if (url) {
                presetInput(url);
            }
        }

        if (Object.keys(props).length && props.search !== undefined) {
            presetInput(props.search);
        }

    }, []);

    useEffect(() => {
        const handleFocus = () => inputRef.current?.focus();

        window.addEventListener('focusSearch', handleFocus);

        return () => {
            window.removeEventListener('focusSearch', handleFocus);
        };
    }, []);

    useEffect(() => {
        cleanExpiredCache();
    }, []);

    window.addEventListener('load', () => {
        if (typeof monitoring_host !== 'undefined' && monitoring_host !== '') {
            setQuery(monitoring_host);
        }
    });

    useEffect(() => {
        document.addEventListener('keydown', onKeydown);
        return () => {
            document.removeEventListener('keydown', onKeydown);
        };
    }, []);

    useEffect(() => {
        if (activeLine < 0) {
            setActiveLine(null);
        }
        if (lineNumber > 0 && activeLine >= lineNumber) {
            setActiveLine(lineNumber - 1);
        }
    }, [activeLine, lineNumber]);

    useEffect(() => {
        setLineNumber(0);
        setActiveLine(null);
        const queryType = getValidations(query);
        if (queryType.domain || queryType.email) {
            setLineNumber(2);
        }
        if (queryType.ip) {
            setLineNumber(4);
        }
        return () => {
            setLineLinks({});
        }
    }, [query]);

    const onKeydown = (e) => {
        const key = e.key;
        switch (key) {
            case 'ArrowUp':
                e.preventDefault();
                setActiveLine(activeLine => activeLine - 1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                setActiveLine(activeLine => activeLine === null ? 0 : activeLine + 1);
                break;
        }
    };

    const handleClickOutside = () => {
        if (isDesktop) {
            setTrigger(false);
            if (headerBlock) {
                headerBlock.classList.remove(searchIsActiveCssClass);
            }
        }
    };

    const handleInput = (e) => {
        setQuery(e.target.value);
        submitQuery(e.target.value);
    }

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (activeLine !== null) {
            if (lineLinks[activeLine]) {
                window.location.href = lineLinks[activeLine];
            }
        }
        else {
            checkPath(query.trim());
            submitQuery();
        }
    }

    const submitQuery = (value) => {
        resizeSearchInput(true);
        if(query !== '' || value !== '') {
            setTrigger(true);
        }
    }

    const presetInput = (query) => {
        setQuery(query);
        resizeSearchInput(true);
    }

    const resizeSearchInput = (focus) => {
        if (headerBlock) {
            if (focus) {
                headerBlock.classList.add(searchIsActiveCssClass);
            } else {
                headerBlock.classList.remove(searchIsActiveCssClass);
            }
        }
        setOpened(focus);
    }

    const incrementLine = (value) => {
        setLineNumber(lineNumber => {
            return value > 0 ? lineNumber + value : lineNumber + 1
        });
    }

    return (
        <>
            <button type={'button'}
                    className={cn(styles.cancel, {[styles.hidden]: !opened})}
                    onClick={() => {
                        resizeSearchInput(false);
                    }}>
                <svg className="size-24 icon">
                    <use href="/webpack/dist/sprite.svg#icon-close"></use>
                </svg>
            </button>
            <button type="button" className={cn(styles.trigger, {[styles.hidden]: opened})} ref={mobileTriggerRef}
                    onClick={() => {
                        resizeSearchInput(true)
                    }}>
                <svg className="icon size-24">
                    <use href="/webpack/dist/sprite.svg#icon-search"></use>
                </svg>
                <span className="visually-hidden">mobile search trigger</span>
            </button>
            <div className={cn(styles.wrapper, {[styles.opened]: opened})}>
                <div className={styles.container}>
                    {trigger && <div className={styles.shadow}></div>}
                    <form className={styles.form} onSubmit={handleSubmit} ref={formRef} autoComplete={'off'}>
                        <ClickOutside refElement={inputRef}
                                      onOutsideClick={() => handleClickOutside()}
                                      exceptElements={formRef}
                        />
                        <input
                            className={styles.input}
                            type="text"
                            ref={inputRef}
                            placeholder="Search IPs, emails, sites, APIs, plugins, and solutions across cleantalk.org."
                            value={query}
                            onChange={handleInput}
                            onFocus={() => {
                                resizeSearchInput(true)
                                if (query.trim() !== '' ) {
                                    setTrigger(true);
                                }
                            }}
                            autoComplete={'off'}
                        />

                        <button type={'submit'} ref={submitRef}
                                className={styles.submit}
                                onClick={(ev) => {
                                    handleSubmit(ev);
                                }}>
                            <svg className={styles.loupe}>
                                <use href="/webpack/dist/sprite.svg#icon-search"></use>
                            </svg>
                            <span className={'visually-hidden'}>click to trigger searching</span>
                        </button>

                        {query !== "" &&
                            <button type={'button'} ref={clearRef}
                                    className={styles.close}
                                    onClick={() => {
                                        setQuery("");
                                        setTrigger(false);
                                    }}>
                                <svg className="size-22 icon">
                                    <use href="/webpack/dist/sprite.svg#icon-close"></use>
                                </svg>
                            </button>
                        }

                        {
                            (trigger) && (
                                <>

                                    <div ref={ref} className={styles.resultContainer}>
                                        <div className={styles.resultInner}>

                                            <SwitchSearch query={query.trim()} trigger={trigger} activeLine={activeLine}
                                                          setLineLinks={setLineLinks}/>

                                            <ApiSearch query={query.trim()} trigger={trigger} lineNumber={lineNumber}
                                                          incrementLine={incrementLine} activeLine={activeLine}
                                                          setLineLinks={setLineLinks}/>
                                        </div>
                                    </div>
                                </>
                            )
                        }
                    </form>
                </div>
            </div>
        </>
    );
};

export default Search;
